import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { I18nService } from '../../../core/services/i18n.service';

/**
 * Language Selector Component
 * Allows users to switch between supported languages
 */
@Component({
  selector: 'app-language-selector',
  standalone: true,
  imports: [
    CommonModule,
    MatToolbarModule,
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  template: `
    <button
      mat-icon-button
      [matMenuTriggerFor]="menu"
      class="language-selector"
      [disabled]="isLoading$ | async"
    >
      <div class="loading-container">
        <mat-spinner *ngIf="isLoading$ | async" diameter="20" strokeWidth="2"></mat-spinner>
        <mat-icon *ngIf="!(isLoading$ | async)">language</mat-icon>
      </div>
      <span class="language-text">{{ currentLanguage$ | async }}</span>
    </button>
    <mat-menu #menu="matMenu">
      <button
        mat-menu-item
        *ngFor="let lang of supportedLanguages"
        (click)="selectLanguage(lang)"
        [class.active]="lang === (currentLanguage$ | async)"
        [disabled]="isLoading$ | async"
      >
        {{ getLanguageName(lang) }}
      </button>
    </mat-menu>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .language-selector {
        display: flex;
        align-items: center;
        gap: 8px;
        color: inherit;
        margin-right: 50px;
      }

      .language-selector[disabled] {
        opacity: 0.7;
        cursor: not-allowed;
      }

      .language-selector mat-icon {
        color: inherit;
      }

      .loading-container {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
      }

      .language-text {
        margin-left: -4px;
      }

      .active {
        font-weight: bold;
        background-color: rgba(0, 0, 0, 0.04);
      }
    `,
  ],
})
export class LanguageSelectorComponent implements OnInit {
  private i18nService = inject(I18nService);

  currentLanguage$ = this.i18nService.getCurrentLanguage$();
  isLoading$ = this.i18nService.getIsLoading$();
  supportedLanguages: string[] = [];

  ngOnInit(): void {
    this.supportedLanguages = this.i18nService.getSupportedLanguages();
  }

  selectLanguage(language: string): void {
    this.i18nService.setLanguage(language);
  }

  getLanguageName(language: string): string {
    return this.i18nService.getLanguageName(language);
  }
}
